import { Crop, Layers2, Sparkles } from "lucide-react";

import { defaultModel, draftFor, maxOutputs, modelsFor, typeLabel } from "../draft";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Choice } from "../../../ui/Choice";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import type { Composer } from "./use-composer";

// The settings both composers show, in live's order: what to make (when there is a choice),
// the model, the ratio, and after the video's own settings the count. Each takes the composer.

export function TypeChoice({ c }: { c: Composer }) {
  // A tool's form makes its own type (#209): live shows no type choice there.
  if (c.tool || c.types.length <= 1) return null;
  return (
    <Choice
      setting="What to make"
      icon={<Sparkles size={20} strokeWidth={1.5} />}
      value={c.type.id}
      options={c.types.map((each) => ({ value: each.id, label: typeLabel(each.id) }))}
      onChange={(next) => {
        const chosen = c.types.find((each) => each.id === next);
        if (chosen !== undefined) {
          c.set(
            draftFor(
              chosen,
              defaultModel(modelsFor(c.catalog, chosen)),
              c.draft.prompt,
              c.source,
              c.draft.endFrame,
            ),
          );
        }
      }}
    />
  );
}

export function ModelChoice({ c }: { c: Composer }) {
  return (
    <ModelPicker
      models={c.models}
      value={c.model.id}
      onChange={(next) => {
        const chosen = c.models.find((each) => each.id === next);
        if (chosen !== undefined) c.chooseModel(chosen);
      }}
    />
  );
}

export function RatioChoice({ c }: { c: Composer }) {
  // A tool's job takes no frame: its ratio is the model's own.
  const framed = !c.tool && (c.source !== null || c.draft.endFrame !== null);
  // A video's ratios are the footer's: live's for the modes left (issue 366); an image's, its row's.
  // A tweak's starts at the image's own, as live's Tweak form (issue 678).
  const ratio =
    c.footer !== null
      ? c.footer.ratio
      : c.tweak
        ? (c.draft.aspectRatio ?? c.imageRatio)
        : c.draft.aspectRatio;
  const values = c.footer === null ? c.model.aspect_ratios.values : c.footer.options.ratio.values;
  // With a frame the video keeps its shape, and where a mode left takes no ratio the model
  // decides: live's ratio then reads "auto", with a crop icon. Live's Tweak names the image's
  // ratio; until its job is read, ours keeps the image's shape as any change does.
  const named = c.tweak && c.imageRatio !== null;
  const auto = (framed && !named) || (c.footer?.options.ratio.auto ?? false);
  return (
    <Choice
      setting="Aspect ratio"
      icon={
        c.video !== undefined && (ratio === null || ratio === "auto") ? (
          <Crop size={20} strokeWidth={1.5} />
        ) : (
          <RatioGlyph ratio={ratio} />
        )
      }
      width={100}
      chevron={c.video === undefined}
      value={ratio ?? ""}
      options={[
        ...(auto ? [{ value: "", label: c.video === undefined ? "Same as the image" : "auto" }] : []),
        ...values.map((each) => ({ value: each, label: each })),
      ]}
      onChange={(next) => c.set({ aspectRatio: next || null })}
    />
  );
}

export function OutputsChoice({ c }: { c: Composer }) {
  return (
    <Choice
      setting="Outputs"
      icon={<Layers2 size={20} strokeWidth={1.5} />}
      width={80}
      // Live's Tweak form shows its count of 1, which nothing changes, with this tooltip (issue
      // 678).
      chevron={c.video === undefined && !c.tweak}
      disabledBecause={
        c.tweak ? "Number of images generated with the current settings." : undefined
      }
      value={String(c.draft.outputs)}
      options={Array.from({ length: c.tweak ? 1 : maxOutputs(c.type, c.model) }, (_, at) => ({
        value: String(at + 1),
        label: String(at + 1),
      }))}
      onChange={(next) => c.set({ outputs: Number(next) })}
    />
  );
}
